import { useEmpathyAdapter } from '@/adapters/adapter.configurator';
import { mapFilterColorUrl } from '@/adapters/gocco-filter.mapper';
import { GoccoResultMapper } from '@/adapters/gocco-result.mapper';
import BackgroundClose from '@/components/gocco-background-close.vue';
import { applyMixins } from '@/config/configurator/apply-mixins.configurator';
import { createPortalsPlaceholders } from '@/config/configurator/create-portals-placeholders';
import { rebindContainer } from '@/config/configurator/rebind-container.configurator';
import { registerComponents } from '@/config/configurator/register-components.configurator';
import { DESKTOP_CORE_CONFIG, MOBILE_CORE_CONFIG } from '@/config/core.config';
import { DESKTOP_EMPATHIZE_CONFIG, MOBILE_EMPATHIZE_CONFIG } from '@/config/empathize.config';
import { DESKTOP_FACETS_CONFIG, MOBILE_FACETS_CONFIG } from '@/config/facets.config';
import { DESKTOP_NEXT_QUERIES_CONFIG, MOBILE_NEXT_QUERIES_CONFIG } from '@/config/next-queries.config';
import { DESKTOP_PARTIALS_CONFIG, MOBILE_PARTIALS_CONFIG } from '@/config/partials.config';
import { MOBILE_RELATED_TAGS_CONFIG } from '@/config/related-tags.config';
import { DESKTOP_RESULTS_CONFIG } from '@/config/results.config';
import { MOBILE_SEARCH_BOX_CONFIG } from '@/config/search-box.config';
import { MOBILE_SORT_CONFIG } from '@/config/sort.config';
import { STORE_CONFIG } from '@/config/store.config';
import { DESKTOP_LOCALE_CONFIG } from '@/i18n/desktop/locale.config';
import { MOBILE_LOCALE_CONFIG } from '@/i18n/mobile/locale.config';
import { COMPONENTS, ConfigBuilder, EVENTS, installEmpathyX, WIRES } from '@empathybroker/ebx-gluon';
import App from './App.vue';

installEmpathyX(appBuilder => {
  createPortalsPlaceholders();
  appBuilder
    .overrideStore(STORE_CONFIG)
    .setAdapterFactory(useEmpathyAdapter({
      configureAdapter(adapterbuilder) {
        adapterbuilder
          .addClassMapper(GoccoResultMapper, 'results')
          .addMapper(mapFilterColorUrl, 'filters')
          .setFacetConfig({ prefix: { facetName: 'colorName_facet', noTagFacetName: 'colorName_facet' } }, 'color_facet')
          .setFacetConfig({ prefix: { facetName: 'size_facet', noTagFacetName: 'size_facet' } }, 'sizes_facet')
          .setFacetConfig({ prefix: { facetName: 'price', noTagFacetName: 'price_facet' } }, 'price_facet');
      }
    }))

    .setAppComponent(App)
    .setDynamicConfig({
      endpoints: {
        search: 'api{env}.empathybroker.com/search/v1/query/gocco/searchX',
      }
    })
    .addPortal(BackgroundClose, '#ebx-external-background-close')
    // .addPortal(Components.Smart.Close, '.ebx-header-close') Looks like this was from the first designs when we keeped their header
    .addWireToEvent(WIRES.blurSearchInput, EVENTS.Scroll)
    .addWireToEvent((observable, { store, emit }) => {
      observable.subscribe((panelId: string) => {
        if (panelId === 'all-facets' && store.state.config.device === 'desktop') {
          const columns = store.state.search.facetsGroups['all-facets'].collapsed ? 4 : 3;
          emit(EVENTS.ColumnNumberChanged, columns);
        }
      });
    }, EVENTS.TogglePanel)
    .setCustomConfigurator(configuratorOptions => {
      registerComponents();
      rebindContainer(configuratorOptions);
      applyMixins();
    })
    .setConfigFor('mobile', (configBuilder: ConfigBuilder) => {
      configBuilder
        .setCoreConfig(MOBILE_CORE_CONFIG)
        .setRecommendationsConfig({ gridColumns: 2 })
        .setLocaleConfig(MOBILE_LOCALE_CONFIG)
        .setFacetsConfig(MOBILE_FACETS_CONFIG)
        .setEmpathizeConfig(MOBILE_EMPATHIZE_CONFIG)
        .setIdentifierSuggestionsConfig({
          regex: { identifierDetection: '^\\D\\d+\\w*' }, size: 4
        })
        .setHistoryConfig({
          queryHistorySize: 5,
          components: {
            toggle: COMPONENTS.PURE.SimpleSlideToggle
          }
        })
        .setNextQueriesConfig(MOBILE_NEXT_QUERIES_CONFIG)
        .setNextQueriesSuggestionsConfig({ size: 5 })
        .setOpenConfig({ triggersSelector: '.form-search' })
        .setPartialsConfig(MOBILE_PARTIALS_CONFIG)
        .setRelatedTagsConfig(MOBILE_RELATED_TAGS_CONFIG)
        .setSearchBoxConfig(MOBILE_SEARCH_BOX_CONFIG)
        .setSortConfig(MOBILE_SORT_CONFIG)
        .setSuggestionsConfig({ components: { item: 'GoccoTermSuggestion' } })
        .setPopularSearchesConfig({ size: 5 })
        .setScrollConfig({
          scrollPanelSelector: '.ebx-empathy-x__body'
        });
    })
    .setConfigFor('desktop', (configBuilder: ConfigBuilder) => {
      configBuilder
        .setLocaleConfig(DESKTOP_LOCALE_CONFIG)
        .setCoreConfig(DESKTOP_CORE_CONFIG)
        .setEmpathizeConfig(DESKTOP_EMPATHIZE_CONFIG)
        .setFacetsConfig(DESKTOP_FACETS_CONFIG)
        .setNextQueriesConfig(DESKTOP_NEXT_QUERIES_CONFIG)
        .setOpenConfig({ triggersSelector: '.mini-search' })
        .setRecommendationsConfig({ gridColumns: 4 })
        .setResultsGridConfig({ grid: { columns: 3 } })
        .setSuggestionsConfig({ size: 3 })
        .setResultsConfig(DESKTOP_RESULTS_CONFIG)
        .setPartialsConfig(DESKTOP_PARTIALS_CONFIG);
    });
});
